import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { LanguageSelect } from "@/components/LanguageSelect";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const { login, register, user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", username: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) nav("/app"); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (isLogin) await login(form.email, form.password);
      else await register(form.name, form.email, form.password, form.username);
      toast.success(isLogin ? t("auth.welcome") : t("auth.created"));
      nav("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Zine photo panel */}
      <div className="relative hidden lg:block border-e-2 border-foreground">
        <img
          src="https://images.unsplash.com/photo-1706675780107-7c43cc487928?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="stadium"
          className="w-full h-full object-cover zine-img"
        />
        <div className="absolute inset-0 bg-primary/20 mix-blend-multiply" />
        <div className="absolute top-8 start-8">
          <span className="font-data text-[10px] uppercase tracking-[0.25em] bg-background border-2 border-foreground px-3 py-1.5">Team Sheet</span>
        </div>
        <div className="absolute bottom-12 start-8 end-8">
          <p className="overline text-primary mb-3">{t("auth.panelOverline")}</p>
          <p className="font-display text-5xl uppercase leading-[0.9] max-w-md">{t("auth.panelText")}</p>
        </div>
      </div>

      {/* Coupon form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center justify-between mb-10 pb-4 border-b-2 border-foreground">
            <Link to="/" className="flex items-baseline gap-1">
              <span className="font-display text-3xl tracking-tight">GAFFER<span className="text-primary">.</span></span>
            </Link>
            <LanguageSelect />
          </div>
          <p className="overline text-primary mb-2">{isLogin ? "Sign in" : "Register"}</p>
          <h1 className="font-display text-5xl uppercase leading-none">{isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-3 mb-8">
            {isLogin ? t("auth.loginSub") : t("auth.registerSub")}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name" className="overline text-muted-foreground">{t("auth.name")}</Label>
                <Input id="name" data-testid="name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 font-data" />
              </div>
            )}
            {!isLogin && (
              <div>
                <Label htmlFor="username" className="overline text-muted-foreground">Username (public)</Label>
                <Input id="username" data-testid="username-input" required value={form.username}
                  placeholder="Shown on leaderboards" maxLength={24}
                  onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1.5 h-11 font-data" />
                <p className="text-[11px] text-muted-foreground mt-1">Your real name stays private — this is what other gaffers see.</p>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="overline text-muted-foreground">{t("auth.email")}</Label>
              <Input id="email" type="email" data-testid="email-input" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11 font-data" />
            </div>
            <div>
              <Label htmlFor="password" className="overline text-muted-foreground">{t("auth.password")}</Label>
              <Input id="password" type="password" data-testid="password-input" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11 font-data" />
              {isLogin && (
                <Link to="/forgot-password" data-testid="forgot-password-link"
                  className="inline-block mt-1.5 text-xs text-muted-foreground hover:text-primary underline underline-offset-4">
                  Forgot password?
                </Link>
              )}
            </div>
            {error && <p className="text-destructive text-sm font-data" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="auth-submit-btn"
              className="w-full h-12 text-base">
              {loading ? t("auth.wait") : isLogin ? t("auth.loginBtn") : t("auth.signupBtn")}
            </Button>
          </form>

          <p className="text-muted-foreground text-sm mt-6">
            {isLogin ? t("auth.noAccount") : t("auth.already")}
            <Link to={isLogin ? "/register" : "/login"} className="text-primary font-bold underline underline-offset-4" data-testid="auth-switch-link">
              {isLogin ? t("auth.toSignup") : t("auth.toLogin")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
